import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Compass,
  HardHat,
  Cpu,
  ShieldCheck,
  Globe2,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Marquee from "@/components/landing/Marquee";
import ConsultationForm from "@/components/landing/ConsultationForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    icon: Compass,
    number: "01",
    title: "Cross-Border Landlord Advisory",
    body:
      "Data-driven yield optimisation, spatial repositioning and municipal compliance for premium urban residential assets.",
    points: ["Micro-market yield analysis", "Interior arbitrage design", "Regulatory compliance audit"],
  },
  {
    icon: HardHat,
    number: "02",
    title: "Remote Project Coordination",
    body:
      "Vetted contractors, milestone monitoring and material billing audits — your virtual boots-on-the-ground.",
    points: ["Contractor sourcing & vetting", "Quality & milestone control", "Vendor billing audits"],
  },
  {
    icon: Cpu,
    number: "03",
    title: "PropTech Infrastructure & Retainers",
    body:
      "PMS integration, smart access, turnaround logistics and ongoing remote performance auditing.",
    points: ["PMS & smart-lock onboarding", "Cleaning & turnaround logistics", "Performance indices & Xero audit"],
  },
];

const principles = [
  {
    icon: ShieldCheck,
    title: "Asset-Light, Zero Custody",
    body: "We never hold client funds, escrow, or construction liability. Strictly B2B fee-for-service.",
  },
  {
    icon: Globe2,
    title: "Tier-1 City Focus",
    body: "Soho, London W1D, Dubai Marina, Upper East Side — premium corridors only.",
  },
  {
    icon: Sparkles,
    title: "Data-Driven Yield",
    body: "Every recommendation is grounded in micro-market analytics and measurable returns.",
  },
];

export default function Landing() {
  const [healthOk, setHealthOk] = useState(true);

  useEffect(() => {
    axios.get(`${API}/`).catch(() => setHealthOk(false));
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0B0E14] text-[#F8F9FA]" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section
        id="hero"
        className="relative pt-32 pb-24 sm:pt-40 sm:pb-32"
        data-testid="hero-section"
      >
        {/* Atmospheric backdrop */}
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODEzMjUyMDl8MA&ixlib=rb-4.1.0&q=85"
            alt="Luxury interior"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/70 via-[#0B0E14]/85 to-[#0B0E14]" />
        </div>

        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto fade-up">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <p className="overline mb-6" data-testid="hero-overline">
                Grand Venture FZ LLC · United Arab Emirates
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                Quiet stewardship of <em className="text-[#C5A059] not-italic">premium residential</em> capital,
                <br className="hidden sm:block" />
                across continents.
              </h1>
              <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#A0AAB2] leading-relaxed">
                We advise high-net-worth international investors and overseas landlords on yield optimisation,
                remote project oversight and PropTech-enabled portfolio management — without ever touching
                your funds or assuming construction liability.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <a
                  href="#contact"
                  data-testid="hero-cta-schedule"
                  className="group inline-flex items-center gap-3 bg-[#C5A059] text-[#0B0E14] px-7 py-4 hover:bg-[#D4AF37] transition-colors duration-300"
                >
                  <span className="font-medium tracking-wide text-sm uppercase">Schedule a consultation</span>
                  <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href="#services"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 text-[#F8F9FA]/80 hover:text-[#C5A059] transition-colors duration-300 text-sm uppercase tracking-wider"
                >
                  Explore services <ChevronRight size={16} />
                </a>
              </div>
            </div>

            <div className="lg:col-span-4 border-l border-white/10 pl-8 hidden lg:block">
              <p className="overline mb-4">Mandate</p>
              <p className="font-serif text-2xl leading-snug text-[#F8F9FA]/90">
                Eliminating geographic friction for foreign owners of urban residential assets in Tier-1 global cities.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-6">
                <div>
                  <div className="font-serif text-4xl text-[#C5A059]">15<span className="text-xl align-top">+</span></div>
                  <p className="text-xs uppercase tracking-widest text-[#A0AAB2] mt-1">Years experience</p>
                </div>
                <div>
                  <div className="font-serif text-4xl text-[#C5A059]">100<span className="text-xl align-top">%</span></div>
                  <p className="text-xs uppercase tracking-widest text-[#A0AAB2] mt-1">B2B fee-for-service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial marquee */}
      <Marquee />

      {/* SERVICES */}
      <section id="services" className="py-28 sm:py-36 relative grain" data-testid="services-section">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-20">
            <div className="lg:col-span-5">
              <p className="overline mb-5">Services · 01—03</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Three disciplines.<br />
                <span className="text-[#C5A059] italic">One discreet partner.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-[#A0AAB2] leading-relaxed text-base">
                Built around the operational gaps that frustrate cross-border landlords: misaligned local
                agents, fragmented vendor oversight, and the absence of measurable performance signals.
                We close all three — quietly, and on retainer.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.number}
                  className="group bg-[#0B0E14] p-10 lg:p-12 transition-colors duration-500 hover:bg-[#151A22]"
                  data-testid={`service-card-${s.number}`}
                >
                  <div className="flex items-start justify-between mb-10">
                    <span className="font-serif text-5xl text-[#C5A059]/80">{s.number}</span>
                    <Icon size={24} strokeWidth={1} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl mb-4 leading-tight">{s.title}</h3>
                  <p className="text-sm text-[#A0AAB2] leading-relaxed mb-8">{s.body}</p>
                  <ul className="space-y-3 border-t border-white/10 pt-6">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-[#F8F9FA]/85">
                        <span className="mt-1.5 w-3 h-px bg-[#C5A059] flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE — asymmetric */}
      <section id="audience" className="py-28 sm:py-36 border-t border-white/5" data-testid="audience-section">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 relative">
              <img
                src="https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBjaXR5JTIwc2t5bGluZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODEzMjUyMDl8MA&ixlib=rb-4.1.0&q=85"
                alt="London skyline"
                className="w-full h-[460px] object-cover grayscale contrast-110"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#C5A059] text-[#0B0E14] px-6 py-4 hidden sm:block">
                <p className="overline" style={{ color: "#0B0E14" }}>Pipeline</p>
                <p className="font-serif text-xl mt-1">Soho · W1D</p>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <p className="overline mb-5">Who we serve</p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08]">
                For the investor who owns property abroad —
                <span className="text-[#C5A059] italic"> but rarely walks through it.</span>
              </h2>
              <p className="mt-8 text-[#A0AAB2] leading-relaxed">
                Our mandate is built for high-net-worth individuals, overseas private landlords, and boutique
                offshore real-estate syndicates holding residential assets in prime international corridors.
              </p>
              <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-5">
                {[
                  "Frustrated by misaligned local agents",
                  "Seeking measurable rental yield growth",
                  "Concerned about regulatory exposure",
                  "Need a single trusted operational partner",
                ].map((line) => (
                  <div key={line} className="flex items-start gap-3 text-sm text-[#F8F9FA]/85">
                    <span className="mt-2 w-2 h-2 bg-[#C5A059] rotate-45 flex-shrink-0" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US — bento */}
      <section id="why" className="py-28 sm:py-36 bg-[#0E1219]" data-testid="why-section">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <p className="overline mb-5">Operating principles</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Built on restraint, <br />
                <span className="text-[#C5A059] italic">measured by yield.</span>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {principles.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-[#151A22] border border-white/10 p-10 hover:border-[#C5A059]/40 hover:-translate-y-1 transition-all duration-500"
                  data-testid={`principle-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={28} strokeWidth={1} className="text-[#C5A059] mb-8" />
                  <h3 className="font-serif text-2xl mb-3 leading-tight">{p.title}</h3>
                  <p className="text-sm text-[#A0AAB2] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section id="founder" className="py-28 sm:py-36 border-t border-white/5" data-testid="founder-section">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="overline mb-5">A word from the founder</p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08] mb-8">
                "Geography should never be the reason an asset under-performs."
              </h2>
              <p className="text-[#A0AAB2] leading-relaxed mb-4">
                For fifteen years I have stood between international owners and the cities their assets live in —
                Soho, Marylebone, Mayfair, the Marina. The model is simple: align incentives, instrument the property,
                and report with the candour an institutional desk would demand.
              </p>
              <p className="text-[#A0AAB2] leading-relaxed mb-8">
                We work with a small circle of clients, on retainer, in writing.
              </p>
              <div className="border-t border-white/10 pt-6">
                <p className="font-serif text-xl">Stefan Nagel</p>
                <p className="text-xs uppercase tracking-widest text-[#C5A059] mt-1">Founder & Principal</p>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2 relative">
              <img
                src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMjd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzc21hbiUyMHBvcnRyYWl0JTIwc3VpdHxlbnwwfHx8fDE3ODEzMjUyMDl8MA&ixlib=rb-4.1.0&q=85"
                alt="Stefan Nagel"
                className="w-full h-[560px] object-cover grayscale contrast-110"
              />
              <div className="absolute top-6 left-6 border border-[#C5A059] px-4 py-2">
                <span className="overline">Est. UAE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-28 sm:py-36 bg-[#0E1219] border-t border-white/5" data-testid="contact-section">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <p className="overline mb-5">Begin a conversation</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Request a private <br />
                <span className="text-[#C5A059] italic">consultation.</span>
              </h2>
              <p className="mt-8 text-[#A0AAB2] leading-relaxed max-w-md">
                All enquiries are reviewed personally. We respond within two business days, in writing,
                with a discreet proposal tailored to your portfolio.
              </p>

              <div className="mt-12 space-y-5">
                <div className="flex items-start gap-4">
                  <Mail size={18} className="text-[#C5A059] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#A0AAB2]">Email</p>
                    <a href="mailto:office@grand-venture.com" data-testid="contact-email" className="text-[#F8F9FA] hover:text-[#C5A059] transition-colors">
                      office@grand-venture.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={18} className="text-[#C5A059] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#A0AAB2]">Direct line</p>
                    <a href="tel:+971585397557" data-testid="contact-phone" className="text-[#F8F9FA] hover:text-[#C5A059] transition-colors">
                      +971 58 539 7557
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-[#C5A059] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#A0AAB2]">Headquarters</p>
                    <p className="text-[#F8F9FA] leading-relaxed">
                      VUET3967, Compass Building AL Hulaila<br />
                      AL Hulaila Industrial Zone-FZ<br />
                      Ras Al Khaimah, United Arab Emirates
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <ConsultationForm
                onSubmit={async (payload) => {
                  try {
                    await axios.post(`${API}/consultations`, payload);
                    toast.success("Request received", {
                      description: "We will respond personally within two business days.",
                    });
                    return true;
                  } catch (e) {
                    const detail =
                      e?.response?.data?.detail ||
                      "Unable to submit at the moment. Please try again or email us directly.";
                    toast.error("Submission failed", { description: String(detail) });
                    return false;
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10" data-testid="footer">
        <div className="px-6 sm:px-12 lg:px-24 max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-serif text-xl">Grand Venture FZ LLC</p>
            <p className="text-xs uppercase tracking-widest text-[#A0AAB2] mt-1">
              International residential capital · Established UAE
            </p>
          </div>
          <p className="text-xs text-[#A0AAB2]">
            © {new Date().getFullYear()} Grand Venture FZ LLC. All rights reserved.
          </p>
        </div>
      </footer>

      {!healthOk && (
        <div className="fixed bottom-4 left-4 text-xs text-red-400" data-testid="health-warning">
          API unreachable
        </div>
      )}
    </div>
  );
}
